/**
 * AI CONTEXT
 * Purpose: full-page portfolio view for ONE student (JARVIS-126) — tiles in one
 *   grid, lane chips, the priority lane, three sorts, tags, and a drill-in that
 *   renders a project's steps in the shared ArtworkWall. Replaces the modal as
 *   the entry-point surface; the modal stays as the editor ("Edit project").
 * Route: PORTFOLIO_PAGE_ROUTE (/students/:studentId/portfolio), gated by
 *   ROUTE_PERMISSION_REQUIREMENTS on STUDENT.READ. Entry points run
 *   ensurePortfolioExists BEFORE navigating here, so the doc always exists.
 * Writes: every mutation goes through usePortfolioDbStorage (full-document
 *   persistPortfolio). Drag-drop invariant: the DROP ZONE decides which
 *   reorder runs — grid tile → reorderProjects, lane tile → reorderProjectPriority,
 *   lane → addProjectToPriorityLane, category chip → updateProject({category}).
 *   There is one drag state (`dragProjectId`); no drag-source bookkeeping.
 * See .cursor/skills/portfolios/SKILL.md.
 */
import {
  DownOutlined,
  FormOutlined,
  PlusOutlined,
  UpOutlined,
} from "@ant-design/icons"
import {
  App as AntdApp,
  Avatar,
  Breadcrumb,
  Button,
  Empty,
  Select,
  Spin,
  Typography,
} from "antd"
import {
  type DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import styled from "styled-components"

import { studentAtoms } from "#atoms"
import ArtworkTagSelect from "#blocks/ArtworkTagSelect"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import {
  describeArrangementError,
  filterProjectsByLaneChips,
  getPriorityPosition,
  PORTFOLIO_PRIORITY_LANE_MAX,
  PORTFOLIO_VIEW_SORT,
  PORTFOLIO_VIEW_SORT_OPTIONS,
  type PortfolioLaneChip,
  type PortfolioViewSort,
  projectHasEveryTagId,
  type ProjectPlacement,
  sortProjectsForView,
} from "#components/PortfolioManagement/portfolioArrangement"
import PortfolioManagementModal from "#components/PortfolioManagement/PortfolioManagementModal"
import { categoryLabelMap } from "#components/PortfolioManagement/portfolioPhotoGridConstants"
import PortfolioProjectModal from "#components/PortfolioManagement/PortfolioProjectModal"
import {
  type CreatePortfolioProjectInput,
  type PortfolioMockProject,
  type PortfolioMutationResult,
  usePortfolioDbStorage,
} from "#components/PortfolioManagement/usePortfolioDbStorage"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  PORTFOLIO_CATEGORY,
  type PortfolioCategory,
} from "#customTypes/composedTypes/portfolioTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { useArtworkTags } from "#hooks/newHooks/useArtworkTags"
import { COLORS } from "#styles/colors"
import { resolveArtworkTagTexts } from "#utils/artworkTagHelpers"

import PortfolioDrillIn from "./PortfolioDrillIn"
import PortfolioLaneChips from "./PortfolioLaneChips"
import { getProjectCoverRotation } from "./portfolioPageHelpers"
import { getPortfolioBreadcrumb } from "./portfolioPageRoute"
import PortfolioPriorityLane from "./PortfolioPriorityLane"
import PortfolioProjectTile from "./PortfolioProjectTile"

const { Text } = Typography

const SRoot = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
`

const SChrome = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 20px 10px;
  flex: 0 0 auto;
`

const STitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const SToolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
`

const SBreadcrumbBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 20px;
  border-top: 1px solid ${COLORS.GRAY3};
  border-bottom: 1px solid ${COLORS.GRAY3};
  background: ${COLORS.GRAY1};
  flex: 0 0 auto;
  min-height: 40px;
`

const SBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 12px 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const SGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  align-items: start;
`

const isTextEntryTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  if (target instanceof HTMLInputElement) return true
  if (target instanceof HTMLTextAreaElement) return true
  return target.getAttribute("role") === "combobox"
}

const sideFromPointer = (event: DragEvent<HTMLElement>): ProjectPlacement => {
  const rect = event.currentTarget.getBoundingClientRect()
  return event.clientX < rect.left + rect.width / 2 ? "before" : "after"
}

const EMPTY_CHIPS: ReadonlySet<PortfolioLaneChip> = new Set()

const PortfolioPage = () => {
  const { studentId } = useParams<{ studentId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { message } = AntdApp.useApp()

  const student = useOneDoc<StudentConstructor>(
    studentAtoms,
    DOC_TYPES.STUDENTS,
    studentId ?? null
  )

  const {
    isPortfolioLoaded,
    projects,
    priorityLaneProjectIds,
    addProjectToPriorityLane,
    removeProjectFromPriorityLane,
    reorderProjectPriority,
    reorderProjects,
    updateProject,
    createProject,
  } = usePortfolioDbStorage({ studentId: (studentId ?? null) as Uuid | null })

  const breadcrumb = useMemo(
    () => getPortfolioBreadcrumb(location.state),
    [location.state]
  )

  // --- View state ------------------------------------------------------------

  const [checkedChips, setCheckedChips] =
    useState<ReadonlySet<PortfolioLaneChip>>(EMPTY_CHIPS)
  const [sort, setSort] = useState<PortfolioViewSort>(
    PORTFOLIO_VIEW_SORT.ARRANGEMENT
  )
  const [tagFilter, setTagFilter] = useState<Uuid[]>([])
  const [drillInProjectId, setDrillInProjectId] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const [editorProjectId, setEditorProjectId] = useState<string | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [newProjectOpen, setNewProjectOpen] = useState(false)
  const anyModalOpen = editorOpen || newProjectOpen

  const [dragProjectId, setDragProjectId] = useState<string | null>(null)
  const [gridInsert, setGridInsert] = useState<{
    projectId: string
    side: ProjectPlacement
  } | null>(null)

  const projectsById = useMemo(
    () => Object.fromEntries(projects.map((project) => [project.id, project])),
    [projects]
  )

  const drillInProject = drillInProjectId
    ? (projectsById[drillInProjectId] ?? null)
    : null

  // A deleted project (from the editor) drops the drill-in back to the grid.
  useEffect(() => {
    if (drillInProjectId && !projectsById[drillInProjectId]) {
      setDrillInProjectId(null)
    }
  }, [drillInProjectId, projectsById])

  const laneProjects = useMemo(
    () =>
      priorityLaneProjectIds
        .map((id) => projectsById[id])
        .filter((project): project is PortfolioMockProject => !!project),
    [priorityLaneProjectIds, projectsById]
  )

  const countsByCategory = useMemo(() => {
    const counts: Record<PortfolioCategory, number> = {
      selected: 0,
      sustained: 0,
      paused: 0,
      unassigned: 0,
    }
    projects.forEach((project) => {
      counts[project.category] += 1
    })
    return counts
  }, [projects])

  // The dictionary is global, so the EDITOR offers all of it while the toolbar
  // filter offers only tags this portfolio actually uses — filtering by a tag
  // no project carries can only ever empty the grid.
  const { orderedTags, usedTags, tagsById, createTag } =
    useArtworkTags(projects)

  const tagTextsByProjectId = useMemo(
    () =>
      Object.fromEntries(
        projects.map((project) => [
          project.id,
          resolveArtworkTagTexts(project.tagIds, tagsById),
        ])
      ),
    [projects, tagsById]
  )

  const visibleProjects = useMemo(() => {
    const byChip = filterProjectsByLaneChips(
      projects,
      checkedChips,
      priorityLaneProjectIds
    )
    const byTag = byChip.filter((project) =>
      projectHasEveryTagId(project, tagFilter)
    )
    return sortProjectsForView(byTag, sort, priorityLaneProjectIds)
  }, [checkedChips, priorityLaneProjectIds, projects, sort, tagFilter])

  const coverRotationFor = useCallback(
    (project: PortfolioMockProject) =>
      getProjectCoverRotation(project, student?.artwork),
    [student?.artwork]
  )

  // --- Mutations with the refusal reason surfaced -----------------------------

  const report = useCallback(
    (result: PortfolioMutationResult) => {
      if (!result.ok) {
        message.warning(
          describeArrangementError(result.error, categoryLabelMap)
        )
      }
      return result.ok
    },
    [message]
  )

  const toggleLane = useCallback(
    (projectId: string) => {
      if (priorityLaneProjectIds.includes(projectId)) {
        removeProjectFromPriorityLane(projectId)
      } else if (priorityLaneProjectIds.length >= PORTFOLIO_PRIORITY_LANE_MAX) {
        message.warning(
          `The priority lane holds ${PORTFOLIO_PRIORITY_LANE_MAX} projects. Remove one first.`
        )
      } else {
        addProjectToPriorityLane(projectId)
      }
    },
    [
      addProjectToPriorityLane,
      message,
      priorityLaneProjectIds,
      removeProjectFromPriorityLane,
    ]
  )

  const moveToCategory = useCallback(
    (projectId: string, category: PortfolioCategory) => {
      report(updateProject(projectId, { category }))
    },
    [report, updateProject]
  )

  const handleTagIdsChange = useCallback(
    (projectId: string, tagIds: Uuid[]) => {
      report(updateProject(projectId, { tagIds }))
    },
    [report, updateProject]
  )

  const handleCreateProject = useCallback(
    (input: CreatePortfolioProjectInput) => {
      report(createProject(input))
    },
    [createProject, report]
  )

  // --- Drag state --------------------------------------------------------------

  const handleDragStart = useCallback(
    (_event: DragEvent<HTMLDivElement>, projectId: string) => {
      setDragProjectId(projectId)
    },
    []
  )
  const handleDragEnd = useCallback(() => {
    setDragProjectId(null)
    setGridInsert(null)
  }, [])

  const dragProject = dragProjectId ? projectsById[dragProjectId] : undefined
  const canReorderGrid = sort === PORTFOLIO_VIEW_SORT.ARRANGEMENT

  // --- Navigation ----------------------------------------------------------------

  const openDrillIn = useCallback((projectId: string) => {
    setDrillInProjectId(projectId)
  }, [])
  const backToGrid = useCallback(() => setDrillInProjectId(null), [])

  const openEditor = useCallback((projectId: string | null) => {
    setEditorProjectId(projectId)
    setEditorOpen(true)
  }, [])
  const closeEditor = useCallback(() => setEditorOpen(false), [])

  // Esc ladder: drill-in → grid; collapsed chrome → expand; else back up the
  // breadcrumb. Modals, dropdowns and the wall's own strip Esc are left alone.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return
      if (anyModalOpen || isTextEntryTarget(event.target)) return
      if (drillInProjectId) {
        setDrillInProjectId(null)
        return
      }
      if (collapsed) {
        setCollapsed(false)
        return
      }
      navigate(breadcrumb.backPath)
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [anyModalOpen, breadcrumb.backPath, collapsed, drillInProjectId, navigate])

  const goTo = useCallback(
    (path: string) => (event: React.MouseEvent) => {
      event.preventDefault()
      navigate(path)
    },
    [navigate]
  )

  const breadcrumbItems = useMemo(() => {
    const items: Array<{ title: React.ReactNode }> = [
      {
        title: (
          <a
            href={breadcrumb.root.path}
            onClick={goTo(breadcrumb.root.path)}
            data-testid="portfolio-breadcrumb-root"
          >
            {breadcrumb.root.label}
          </a>
        ),
      },
      { title: student?.name ?? "…" },
    ]
    if (drillInProject) {
      items.push({
        title: (
          <a
            href={location.pathname}
            onClick={(event) => {
              event.preventDefault()
              backToGrid()
            }}
            data-testid="portfolio-breadcrumb-grid"
          >
            Portfolio
          </a>
        ),
      })
      items.push({ title: drillInProject.title })
    } else {
      items.push({ title: "Portfolio" })
    }
    return items
  }, [
    backToGrid,
    breadcrumb.root.label,
    breadcrumb.root.path,
    drillInProject,
    goTo,
    location.pathname,
    student?.name,
  ])

  // --- Render --------------------------------------------------------------------

  const isLoading = !student || !isPortfolioLoaded
  const laneCount = priorityLaneProjectIds.length

  const summary = student
    ? `${projects.length} ${projects.length === 1 ? "project" : "projects"} · Selected ${countsByCategory.selected}/5 · Sustained ${countsByCategory.sustained}/15 · ⚡ ${laneCount}`
    : "Loading student…"

  const renderGrid = () => {
    if (!visibleProjects.length) {
      return (
        <Empty
          description={
            projects.length === 0
              ? "No portfolio projects yet."
              : "No projects match these filters."
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          data-testid="portfolio-empty"
        >
          {projects.length === 0 ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setNewProjectOpen(true)}
            >
              New project
            </Button>
          ) : null}
        </Empty>
      )
    }
    return (
      <SGrid data-testid="portfolio-grid" data-sort={sort}>
        {visibleProjects.map((project) => {
          const isUnassigned =
            project.category === PORTFOLIO_CATEGORY.UNASSIGNED
          return (
            <PortfolioProjectTile
              key={project.id}
              project={project}
              variant="grid"
              tagTexts={tagTextsByProjectId[project.id]}
              priorityIndex={getPriorityPosition(
                priorityLaneProjectIds,
                project.id
              )}
              coverRotation={coverRotationFor(project)}
              isDragging={dragProjectId === project.id}
              insertSide={
                gridInsert?.projectId === project.id ? gridInsert.side : null
              }
              draggable={!isUnassigned}
              laneIsFull={laneCount >= PORTFOLIO_PRIORITY_LANE_MAX}
              onOpen={openDrillIn}
              onEdit={openEditor}
              onToggleLane={toggleLane}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={(event, targetId) => {
                // Drop zone rule: a grid tile only accepts a drop when the grid
                // shows the teacher's arrangement — otherwise the drop position
                // would mean nothing.
                if (
                  !dragProjectId ||
                  dragProjectId === targetId ||
                  !canReorderGrid ||
                  isUnassigned
                ) {
                  return
                }
                event.preventDefault()
                event.dataTransfer.dropEffect = "move"
                setGridInsert({
                  projectId: targetId,
                  side: sideFromPointer(event),
                })
              }}
              onDragLeave={(targetId) =>
                setGridInsert((prev) =>
                  prev?.projectId === targetId ? null : prev
                )
              }
              onDrop={(event, targetId) => {
                if (
                  !dragProjectId ||
                  dragProjectId === targetId ||
                  !canReorderGrid ||
                  isUnassigned
                ) {
                  return
                }
                event.preventDefault()
                event.stopPropagation()
                report(
                  reorderProjects(
                    dragProjectId,
                    targetId,
                    sideFromPointer(event)
                  )
                )
                handleDragEnd()
              }}
            />
          )
        })}
      </SGrid>
    )
  }

  return (
    <Page>
      <SRoot
        data-testid="portfolio-page"
        data-collapsed={collapsed || undefined}
      >
        {collapsed ? null : (
          <SChrome data-testid="portfolio-chrome">
            <STitleRow>
              <Flex gap={12} style={{ alignItems: "center", minWidth: 0 }}>
                <Avatar src={student?.avatar || undefined} size={40}>
                  {student?.name?.[0] ?? "?"}
                </Avatar>
                <Flex col style={{ minWidth: 0 }}>
                  <h1
                    style={{ margin: 0, fontSize: 22, lineHeight: "28px" }}
                    data-testid="portfolio-title"
                  >
                    {student ? `${student.name} · Portfolio` : "Portfolio"}
                  </h1>
                  <Text type="secondary" data-testid="portfolio-count">
                    {summary}
                  </Text>
                </Flex>
              </Flex>
              <Flex gap={8} style={{ alignItems: "center" }}>
                <Button
                  icon={<FormOutlined />}
                  onClick={() => openEditor(drillInProjectId)}
                  disabled={isLoading}
                  data-testid="portfolio-open-editor"
                >
                  Open editor
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setNewProjectOpen(true)}
                  disabled={isLoading}
                  data-testid="portfolio-new-project"
                >
                  New project
                </Button>
              </Flex>
            </STitleRow>

            <SToolbar data-testid="portfolio-toolbar">
              <PortfolioLaneChips
                checked={checkedChips}
                onToggle={(chip) =>
                  setCheckedChips((prev) => {
                    const next = new Set(prev)
                    if (next.has(chip)) next.delete(chip)
                    else next.add(chip)
                    return next
                  })
                }
                countsByCategory={countsByCategory}
                laneCount={laneCount}
                laneMax={PORTFOLIO_PRIORITY_LANE_MAX}
                isDragActive={!!dragProjectId}
                dragCategory={dragProject?.category ?? null}
                onDropOnCategory={(category) => {
                  if (dragProjectId) moveToCategory(dragProjectId, category)
                  handleDragEnd()
                }}
                onDropOnPriority={() => {
                  if (
                    dragProjectId &&
                    !priorityLaneProjectIds.includes(dragProjectId)
                  ) {
                    toggleLane(dragProjectId)
                  }
                  handleDragEnd()
                }}
              />
              <Flex
                gap={8}
                style={{ alignItems: "center", marginLeft: "auto" }}
              >
                {/* No onCreateTag: filtering must not grow the vocabulary. */}
                <ArtworkTagSelect
                  testId="portfolio-tag-filter"
                  ariaLabel="Filter by tag"
                  placeholder="Filter by tag"
                  value={tagFilter}
                  onChange={setTagFilter}
                  options={usedTags}
                  allowClear
                  disabled={usedTags.length === 0}
                  style={{ minWidth: 180 }}
                />
                <span data-testid="portfolio-sort">
                  <Select
                    value={sort}
                    onChange={setSort}
                    options={[...PORTFOLIO_VIEW_SORT_OPTIONS]}
                    style={{ width: 210 }}
                    aria-label="Sort projects"
                  />
                </span>
              </Flex>
            </SToolbar>
          </SChrome>
        )}

        <SBreadcrumbBar data-testid="portfolio-breadcrumb-bar">
          <Breadcrumb
            items={breadcrumbItems}
            data-testid="portfolio-breadcrumb"
          />
          {!drillInProject && !canReorderGrid && dragProjectId ? (
            <Text type="warning" style={{ fontSize: 12 }}>
              Switch to Teacher&apos;s arrangement to drag-reorder the grid
            </Text>
          ) : null}
          <Button
            size="small"
            type="text"
            icon={collapsed ? <DownOutlined /> : <UpOutlined />}
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? "Show controls" : "Hide controls"}
            aria-expanded={!collapsed}
            data-testid="portfolio-collapse-toggle"
          >
            {collapsed ? "Show controls" : "Hide controls"}
          </Button>
        </SBreadcrumbBar>

        {isLoading ? (
          <Flex center style={{ flex: 1 }}>
            <Spin />
          </Flex>
        ) : drillInProject ? (
          <PortfolioDrillIn
            project={drillInProject}
            student={student}
            tagOptions={orderedTags}
            onTagIdsChange={handleTagIdsChange}
            onCreateTag={createTag}
            onEdit={openEditor}
          />
        ) : (
          <SBody>
            <PortfolioPriorityLane
              laneProjects={laneProjects}
              laneIds={priorityLaneProjectIds}
              coverRotationFor={coverRotationFor}
              activeProjectId={null}
              dragProjectId={dragProjectId}
              onOpen={openDrillIn}
              onEdit={openEditor}
              onReorder={reorderProjectPriority}
              onAdd={toggleLane}
              onRemove={removeProjectFromPriorityLane}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            />
            {renderGrid()}
          </SBody>
        )}

        {editorOpen && studentId && student ? (
          <PortfolioManagementModal
            isOpen
            handleCloseAttempt={closeEditor}
            title={`Portfolio — ${student.name}`}
            initialValues={{ id: studentId }}
            initialProjectId={editorProjectId ?? undefined}
          />
        ) : null}
        <PortfolioProjectModal
          open={newProjectOpen}
          onClose={() => setNewProjectOpen(false)}
          onCreateProject={handleCreateProject}
        />
      </SRoot>
    </Page>
  )
}

export default PortfolioPage
